import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { AuthenticationService } from '@core/services/auth.service';
import { PaymentService } from '@core/services/payment.service';
import { AlertComponent } from '@shared/alert/alert.component';
import { LegalModalComponent } from '@shared/legal-modal/legal-modal.component';
import { convertInputToLowercase } from '@core/utils/form.utils';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTimes, faCheck, faEye, faEyeSlash, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import {
  buildRegisterPayload,
  createRegisterForm,
  extractRegisteredCompanyId,
  getPasswordRequirements,
  updateRegisterCompanyNameValidation,
} from '@core/utils/auth-register.utils';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule, FormsModule, AlertComponent, FontAwesomeModule, LegalModalComponent],
  templateUrl: './register.component.html',
  styleUrl: './register.component.css'
})
export class RegisterComponent {
  registerForm: FormGroup;
  show = false;
  toastTitle = '';
  toastMessage = '';
  toastType: 'info' | 'success' | 'warning' | 'alert' = 'info';
  isLoading = false;
  
  currentStep: 'selection' | 'form' = 'form';
  selectedProfileType: 'particular' | 'empresa' | null = 'empresa';

  // Modal de upgrade de tier
  showUpgradeModal = false;
  upgradeInfo: any = null;
  isUpgrading = false;
  pendingRegisterData: any = null;
  
  // Icons
  faTimes = faTimes;
  faCheck = faCheck;
  faEye = faEye;
  faEyeSlash = faEyeSlash;
  faChevronRight = faChevronRight;

  // Password visibility
  showPassword = false;
  showPasswordConfirm = false;

  // Legal modal
  showLegalModal = false;
  legalModalTitle = '';

  openLegalModal(event: Event, title: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.legalModalTitle = title;
    this.showLegalModal = true;
  }

  private companyId: number | null = null;
  private customerId: string = '';

  constructor(
    private formBuilder: FormBuilder, 
    private authService: AuthenticationService,
    private paymentService: PaymentService,
    private router: Router,
  ) {
    this.registerForm = createRegisterForm(this.formBuilder, {
      includeProfileType: true,
      defaultProfileType: 'empresa',
    });
  }

  selectProfileType(type: 'particular' | 'empresa') {
    this.selectedProfileType = type;
    this.registerForm.patchValue({ profileType: type });
    this.updateCompanyNameValidation();
    this.currentStep = 'form';
  }

  onProfileTypeChange() {
    this.updateCompanyNameValidation();
  }

  private updateCompanyNameValidation() {
    updateRegisterCompanyNameValidation(this.registerForm);
  }

  async register(): Promise<void> {
    // Validar formulario de registro
    if (this.registerForm.invalid) {
      this.markFormGroupTouched(this.registerForm);
      return;
    }

    this.isLoading = true;
    let registerData: any = null;

    try {
      // PASO 1: Registrar usuario/empresa y enviar datos al backend
      const isCompany = this.registerForm.get('profileType')?.value === 'empresa';
      registerData = buildRegisterPayload(this.registerForm, isCompany);

      await this.attemptRegisterUser(registerData);
      
    } catch (error: any) {
      this.isLoading = false;
      
      // Manejar error 402 Payment Required - Necesita actualizar plan
      if (error?.status === 402 && error?.error?.upgrade_info) {
        this.pendingRegisterData = registerData;
        this.upgradeInfo = error.error.upgrade_info;
        this.showUpgradeModal = true;
        this.showToast('warning', 'Actualizar Plan Requerido', error?.error?.message || 'Necesitas actualizar tu plan para agregar este usuario.');
        return;
      }
      
      // Manejar error 403 Forbidden - No tiene permiso
      if (error?.status === 403) {
        const errorMessage = error?.error?.message || 'No tienes permiso para agregar usuarios en este momento.';
        this.showToast('alert', 'Acceso Denegado', errorMessage);
        return;
      }
      
      const errorMessage = error?.error?.message || error?.message || 'Error al procesar el registro';
      this.showToast('alert', 'Error', errorMessage);
    }
  }

  private async attemptRegisterUser(registerData: any): Promise<void> {
    // PASO 1: Crear Company (ACTIVA desde el inicio)
    const registerResponse = await this.authService.register(registerData).toPromise();
    
    this.companyId = extractRegisteredCompanyId(registerResponse);
    
    if (!this.companyId) {
      this.isLoading = false;
      this.showToast('alert', 'Error', 'Error al obtener información de la empresa registrada.');
      return;
    }

    // PASO 2: Crear Stripe Customer (para futuras facturas)
    const stripeCustomerId = registerResponse?.company?.stripe_customer_id;
    let paymentMethodId = '';
    
    if (stripeCustomerId) {
      this.customerId = stripeCustomerId;
      paymentMethodId = registerResponse?.company?.payment_method_id || '';
    } else {
      const customerResponse = await this.paymentService.createPaymentCustomer(this.companyId).toPromise();
      this.customerId = customerResponse?.data?.stripe_customer_id || customerResponse?.stripe_customer_id || '';
      paymentMethodId = customerResponse?.data?.payment_method_id || customerResponse?.payment_method_id || '';
      
      if (!this.customerId) {
        this.isLoading = false;
        this.showToast('alert', 'Error', 'Error al configurar el sistema de pagos. Contacta con soporte.');
        return;
      }
    }

    // PASO 3: Crear Subscription (tier gratuito o trial)
    // OMITIDO: createSubscription no se ejecuta en el flujo de registro
    // ya que el stripe_subscription_id generado ("free_136_...") no existe en Stripe

    // PASO 4: Enviar email de verificación (manejado por el backend)
    this.isLoading = false;

    // Obtener el email del formulario para pasarlo a la pantalla de verificación
    const userEmail = this.registerForm.get('email')?.value;

    // PASO 5: Redirigir a la pantalla de verificación de email
    this.router.navigate(['/verify-email'], {
      queryParams: {
        email: userEmail
      }
    });
  }

  async confirmUpgrade(): Promise<void> {
    if (!this.upgradeInfo || !this.pendingRegisterData) {
      return;
    }

    this.isUpgrading = true;

    try {
      // Hacer upgrade del tier
      const upgradeResponse = await this.authService.upgradeSubscription(this.upgradeInfo.tier_level).toPromise();
      
      if (upgradeResponse?.success) {
        this.showToast('success', 'Plan Actualizado', `Tu plan ha sido actualizado a ${this.upgradeInfo.tier_name}`);
        this.showUpgradeModal = false;
        this.upgradeInfo = null;
        
        // Reintentar crear el usuario con el nuevo plan
        this.isLoading = true;
        await this.attemptRegisterUser(this.pendingRegisterData);
      } else {
        this.showToast('alert', 'Error', 'No se pudo actualizar el plan. Intenta de nuevo.');
      }
    } catch (error: any) {
      const errorMessage = error?.error?.message || error?.message || 'Error al actualizar el plan';
      this.showToast('alert', 'Error', errorMessage);
    } finally {
      this.isUpgrading = false;
    }
  }

  cancelUpgrade(): void {
    this.showUpgradeModal = false;
    this.upgradeInfo = null;
    this.pendingRegisterData = null;
  }

  markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  showToast(type: 'info' | 'success' | 'warning' | 'alert', title: string, message: string) {
    this.toastType = type;
    this.toastTitle = title;
    this.toastMessage = message;
    this.show = true;
    
    setTimeout(() => {
      this.show = false;
    }, 4000);
  }

  convertToLowercase(event: any, fieldName: string): void {
    convertInputToLowercase(event, this.registerForm, fieldName);
  }

  getPasswordRequirements() {
    const password = this.registerForm.get('password')?.value || '';
    return getPasswordRequirements(password);
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  togglePasswordConfirmVisibility(): void {
    this.showPasswordConfirm = !this.showPasswordConfirm;
  }
}
